import { Injectable, ErrorHandler } from '@angular/core';
import { captureException, withScope } from '@sentry/angular';
import { HttpErrorResponse } from '@angular/common/http';
import { SentryService } from '@box-core/services';

@Injectable({ providedIn: 'root' })
export class GlobalErrorHandler implements ErrorHandler {
  private chunkFailedRegExp = /Loading chunk [\d]+ failed/;
  private localStorageSecurityExp =
    /Failed to read the 'localStorage' property from 'Window': Access is denied for this document/;

  constructor(private sentryService: SentryService) {
    this.sentryService.init();
  }

  handleError(error: Error & { originalError?: unknown }): void {
    if (!error) return;
    console.error(error);

    if (this.chunkFailedRegExp.test(error.message)) return this.handleChunkLoadError();
    if (this.localStorageSecurityExp.test(error.message)) return;

    this.handleSentryError(error.originalError ?? error);
  }

  private handleSentryError(actualError: unknown): void {
    if (actualError instanceof HttpErrorResponse) {
      withScope((scope) => {
        scope.setTag('error.type', 'HttpErrorResponse');
        scope.setExtra('url', actualError.url);
        scope.setExtra('status', actualError.status);
        scope.setExtra('statusText', actualError.statusText);
        scope.setExtra('message', actualError.message);
        scope.setExtra('errorBody', actualError.error);

        // 👇 Add this enhancement
        if (actualError.status === 0) {
          scope.setTag('network.error', 'true');
          scope.setExtra('hint', 'Likely CORS error, DNS issue, or client offline');
        }

        captureException(new Error(`[HTTP ${actualError.status}] ${actualError.message}`));
      });
    } else if (actualError instanceof Error) {
      captureException(actualError);
    } else if (actualError && typeof actualError === 'object') {
      withScope((scope) => {
        scope.setTag('error.type', 'Non-Error Object');
        scope.setExtra('details', actualError);
        captureException(new Error('Non-Error object captured'));
      });
    } else {
      captureException(new Error(String(actualError)));
    }
  }

  private handleChunkLoadError(): void {
    // Optionally notify the user before reload
    window.location.reload();
  }
}
